import { useEffect, useRef, type Dispatch, type MutableRefObject, type SetStateAction } from 'react'
import type { ComboVariation, ComboVariationWithPricing, PropostaItem } from '@/types'
import type { LayoutConfig } from '@/utils/dealCalculations'
import {
  buildSlideLayoutForProposalItem,
  getScopeContentSignature,
  resolveProposalItemScope,
  type CatalogPlan,
} from '@/utils/planCatalog'
import { flattenComboSlides } from './computeActivePlanContext'

function resolveActiveProposalItem(
  params: {
    comboVariations: ComboVariation[]
    proposalItems: PropostaItem[]
    variationsWithPricing: ComboVariationWithPricing[]
    activeIndex: number
  },
): PropostaItem | undefined {
  const { comboVariations, proposalItems, variationsWithPricing, activeIndex } = params
  if (comboVariations.length > 0) {
    return flattenComboSlides(variationsWithPricing)[activeIndex]?.item
  }
  return proposalItems[activeIndex] ?? proposalItems[0]
}

/** Sincroniza layout do slide com o plano ativo — sem contaminar coords entre itens. */
export function useScopeCategoryLayout(params: {
  catalogPlans: CatalogPlan[]
  proposalItems: PropostaItem[]
  activeIndex: number
  comboVariations: ComboVariation[]
  variationsWithPricing: ComboVariationWithPricing[]
  /** Layout gravado na proposta. As posições dos blocos fixos vencem o padrão do catálogo. */
  savedLayout?: Partial<LayoutConfig>
  scopeLayoutSignatureRef: MutableRefObject<string>
  setLayout: Dispatch<SetStateAction<LayoutConfig>>
}) {
  const {
    catalogPlans,
    proposalItems,
    activeIndex,
    comboVariations,
    variationsWithPricing,
    savedLayout,
    scopeLayoutSignatureRef,
    setLayout,
  } = params

  const prevActiveIndexRef = useRef(activeIndex)

  useEffect(() => {
    if (!catalogPlans.length) return

    const item = resolveActiveProposalItem({
      comboVariations,
      proposalItems,
      variationsWithPricing,
      activeIndex,
    })
    if (!item) return

    if (prevActiveIndexRef.current !== activeIndex) {
      scopeLayoutSignatureRef.current = ''
      prevActiveIndexRef.current = activeIndex
    }

    const scope = resolveProposalItemScope(item, catalogPlans)
    const signature = `${activeIndex}:${item.catalogPlanId || item.id}:${getScopeContentSignature(scope)}`
    if (signature === scopeLayoutSignatureRef.current) return
    scopeLayoutSignatureRef.current = signature

    setLayout((prev) =>
      buildSlideLayoutForProposalItem(item, catalogPlans, {
        ...savedLayout,
        categoryCoords: prev.categoryCoords,
      }),
    )
  }, [
    catalogPlans,
    proposalItems,
    activeIndex,
    comboVariations.length,
    variationsWithPricing,
    savedLayout,
    scopeLayoutSignatureRef,
    setLayout,
  ])
}
